import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, from, Observable, of } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { N02_LESSON_ID, N02HowNovaWorksService } from "./n02-how-nova-works.service";

export const N05_LESSON_ID = "N05";

/** Recorded with N05's completed lesson state - N05's CompletedAt *is* this milestone. */
export const NOVA_INSIGHTS_INTRODUCED = "nova_insights_introduced";

/** The Insight snackbar card (NovaInsightWidgetComponent) - the anchored prompt points at it (FLIP-493). */
export const N05_ANCHOR = "nova.insight_snackbar";

/** Copy rows N05.01-N05.03, workbook v2.38 Copy sheet (verbatim; the `*` is a literal asterisk). */
export const N05_COPY = {
  heading: "Your Nova Insight",
  paragraphs: [
    "Nova can share a short daily insight based on the patterns she notices in your flip* activity.",
    "Insights are temporary, so they won't be saved after they disappear. You can change when they arrive, " +
      "manage push notifications or turn them off in Settings.",
  ],
};

/**
 * How the Insight carrying N05 was resolved - any of them completes it: the snackbar opened or dismissed,
 * or the anchored prompt's own visible close (the standard informational dismissal).
 */
export type N05Resolution = "insight_opened" | "insight_dismissed" | "prompt_closed";

/**
 * Onboarding lesson N05 "Your Nova Insight" (FLIP-393; spec v2.36 §24.6, workbook v2.38): an
 * explanation attached to the first eligible Insight snackbar (NovaInsightWidgetComponent), which
 * shows it as an anchored prompt pointing at the snackbar (FLIP-493 - it used to render inside it).
 * Informational: the prompt's visible close, or the snackbar's own open / dismiss controls.
 *
 * - The Insight itself is delivered whether or not N05 is: this service only decides whether the
 *   Insight now showing carries the explanation (`checkAttach()`), and never delays or suppresses it.
 * - Eligible while the `onboarding-walkthrough` flag is on, N05's step row is unresolved, N02 has
 *   *completed* (its row's CompletedAt - a dismissed N02 does not count), and no blocking product
 *   modal is open at that moment. While the N02 panel is on screen it waits for it to close and
 *   only then reads N02's row, so N05 follows N02's X (completed) and never shows over the panel
 *   or after Nova was closed on it (dismissed). If N02 is unresolved, or a modal is open, the Insight is delivered
 *   without N05 and N05 stays unresolved: it is only marked started once an Insight is actually
 *   shown with it, so the next Insight carries it. A failed read holds it back.
 * - Completes (permanently, `nova_insights_introduced`) when the Insight carrying it is opened or
 *   dismissed - the widget calls `onResolved()`. Insights expire overnight, and an expired one
 *   resolves nothing.
 */
@Injectable({ providedIn: "root" })
export class N05NovaInsightService {
  private attached = false;
  private started = false;
  private completed = false;

  constructor(
    private router: Router,
    private blockingUiSignal: BlockingUiSignalService,
    private featureFlagService: FeatureFlagService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private interactionTracking: InteractionTrackingService,
    private n02: N02HowNovaWorksService
  ) {
    // Logout emits false - forget this session's state so a different account gets its own check.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.attached = false;
      this.started = false;
      this.completed = false;
    });
  }

  /**
   * Call when an Insight snackbar becomes visible. Emits once: true when this Insight carries N05
   * (the widget then shows the explanation), false when it does not.
   */
  checkAttach(): Observable<boolean> {
    if (this.completed) {
      return of(false);
    }
    return this.paymentsService.subscriptionActive$.pipe(
      filter((active) => active),
      take(1),
      switchMap(() => from(this.featureFlagService.init())),
      switchMap(() => {
        if (!this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)) {
          return of(false);
        }
        // FLIP-527: no Insight is shown while Nova Insights are off, so there is nothing to explain. Belt and
        // braces - the widget does not render without the flag, so this is not normally reached.
        if (!this.featureFlagService.isEnabled(FeatureFlagKeys.NovaInsights)) {
          return of(false);
        }
        // Wait out the N02 panel: its row only reads "completed" once its X has been pressed.
        return this.n02.visible$.pipe(
          filter((visible) => !visible),
          take(1),
          switchMap(() =>
            forkJoin([
              this.onboardingStepService.getStep(N05_LESSON_ID),
              this.onboardingStepService.getStep(N02_LESSON_ID),
              this.blockingUiSignal.isBlockingUiActive$.pipe(take(1)),
            ])
          )
        ).pipe(
          map(
            ([n05, n02, blocking]) =>
              n05 !== undefined &&
              !n05?.completedAt &&
              !n05?.dismissed &&
              !!n02?.completedAt &&
              !blocking &&
              !this.completed
          )
        );
      }),
      map((attach) => {
        if (attach) {
          this.present();
        }
        return attach;
      })
    );
  }

  /** Call when the Insight carrying N05 is opened or dismissed. No-op if it did not carry N05. */
  onResolved(resolution: N05Resolution): void {
    if (!this.attached || this.completed) {
      return;
    }
    this.completed = true;
    this.attached = false;
    this.onboardingStepService.upsertStep({ stepKey: N05_LESSON_ID, completedAt: new Date() });
    this.track("onboarding_lesson_action", `action: ${resolution}`);
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${NOVA_INSIGHTS_INTRODUCED}`);
  }

  /** N05 is being shown with an Insight: only now does it count as started. */
  private present(): void {
    this.attached = true;
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: N05_LESSON_ID,
      lastViewedAt: now,
      ...(this.started ? {} : { startedAt: now }),
    });
    this.started = true;
    this.track("onboarding_lesson_state_changed", "presented");
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: `lesson: ${N05_LESSON_ID}; ${detail}`,
    });
  }
}
